import { useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { JobFilters, JobBrowseResponse, JobBrowsingState, JobBrowsingAction } from '../types';
import { Job } from '../../../company/JobManagement/model/JobModel';
import { useJobBrowsingController } from '../controllers/useJobBrowsingController';
import React from 'react';

export const initialState: JobBrowsingState = {
  jobs: [],
  filters: {
    query: '',
    location: '',
    job_type: '',
    work_mode: '',
    salary_min: ''
  },
  loading: true,
  error: null,
  page: 0,
  total: 0,
  hasMore: false,
  selectedJob: null,
  showJobModal: false,
  toast: null,
  applyingToJob: null,
  totalPages: 0,
  currentPage: 1,
  searchApplied: false,
  applicationsPerPage: 8,
  viewType: 'grid',
};

 export const jobBrowsingReducer = (state: JobBrowsingState, action: JobBrowsingAction): JobBrowsingState => {
  switch (action.type) {
    case 'SET_JOBS':
      return { ...state, jobs: action.payload };
    case 'APPEND_JOBS':
      return { ...state, jobs: [...state.jobs, ...action.payload] };
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload } };
    case 'RESET_FILTERS':
      return { ...state, filters: initialState.filters };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_PAGE':
      return { ...state, page: action.payload };
    case 'SET_PAGINATION':
      return { ...state, total: action.payload.total, hasMore: action.payload.hasMore };
    case 'SET_SELECTED_JOB':
      return { ...state, selectedJob: action.payload };
    case 'SET_SHOW_JOB_MODAL':
      return { ...state, showJobModal: action.payload };
    case 'SET_TOAST':
      return { ...state, toast: action.payload };
    case 'SET_CURRENT_PAGE':
      return { ...state, currentPage: action.payload };
    case 'SET_TOTAL_PAGES':
      return { ...state, totalPages: action.payload };
    case 'SET_SEARCH_APPLIED':
      return { ...state, searchApplied: action.payload };
    case 'RESET_STATE':
      return initialState;
    case 'SET_VIEW_TYPE':
      return { ...state, viewType: action.payload };
    case 'SET_APPLICATIONS_PER_PAGE':
      return { ...state, applicationsPerPage: action.payload };
    default:
      return state;
  }
};

export const useJobBrowsingViewModel = () => {
  const controller = useJobBrowsingController();
  const [state, dispatch] = useReducer(jobBrowsingReducer, initialState);
  const navigate = useNavigate();

  const fetchJobs = async (goToPage = 1, filtersToUse?: JobFilters, limitOverride?: number) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const page = goToPage;
      const limit = limitOverride ?? state.applicationsPerPage;
       const offset = (page - 1) * limit;

      const params: any = {
        limit,
        offset,
      };

      // Use provided filters or state filters
      const actualFilters = filtersToUse || state.filters;

      if (actualFilters.query) params.query = actualFilters.query;
      if (actualFilters.location) params.location = actualFilters.location;
      if (actualFilters.job_type) params.job_type = actualFilters.job_type;
      if (actualFilters.work_mode) params.work_mode = actualFilters.work_mode;
      if (actualFilters.salary_min) params.salary_min = actualFilters.salary_min;

      const data = await controller.browseJobs(params);

      dispatch({ type: 'SET_JOBS', payload: data.jobs });
      dispatch({ type: 'SET_CURRENT_PAGE', payload: page });

      const totalPages = Math.ceil(data.total / limit);
      dispatch({ type: 'SET_TOTAL_PAGES', payload: totalPages });
      dispatch({ type: 'SET_PAGINATION', payload: { total: data.total, hasMore: data.has_more } });
    } catch (err: any) {
      console.error('Failed to fetch jobs:', err);
      dispatch({ type: 'SET_ERROR', payload: err.response?.data?.message || err.message || 'Failed to fetch jobs' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleFilterChange = (key: keyof JobFilters, value: string) => {
    dispatch({ type: 'SET_FILTERS', payload: { [key]: value } });

    if (key === 'job_type' || key === 'work_mode') {
      // When live filtering, mark search as applied
      dispatch({ type: 'SET_SEARCH_APPLIED', payload: true });
      fetchJobs(1, {
        ...state.filters,
        [key]: value,
      });
    }
  };

  const handleSearch = () => {
    dispatch({ type: 'SET_SEARCH_APPLIED', payload: true });
    fetchJobs(1);
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      query: '',
      location: '',
      job_type: '',
      work_mode: '',
      salary_min: ''
    };

    dispatch({ type: 'SET_FILTERS', payload: emptyFilters });
    dispatch({ type: 'SET_SEARCH_APPLIED', payload: false });

    fetchJobs(1, emptyFilters);
  };


  const hasActiveFilters = React.useMemo(() => {
    // Check if any filter has a value (including empty strings)
    const anyFilterHasValue =
      state.filters.query.trim() !== '' ||
      state.filters.location.trim() !== '' ||
      state.filters.job_type.trim() !== '' ||
      state.filters.work_mode.trim() !== '' ||
      state.filters.salary_min.trim() !== '';

    // If any filter has a value AND search has been applied at least once
    return anyFilterHasValue && state.searchApplied;
  }, [state.filters, state.searchApplied]);


  const goToPage = (page: number) => {
    fetchJobs(page);
  };

  const setCurrentPage = (page: number | ((prev: number) => number)) => {
    if (typeof page === 'function') {
      const newPage = page(state.currentPage);
      fetchJobs(newPage);
    } else {
      fetchJobs(page);
    }
  };


  const viewJobDetails = (job: Job) => {
    navigate(`/jobs/browse/${job.id}`);
  };

  const setViewType = (view: 'grid' | 'list') => {
    dispatch({ type: 'SET_VIEW_TYPE', payload: view });
  };


  useEffect(() => {
    fetchJobs(1);
  }, []);

  return {
    ...state,
    handleFilterChange,
    handleSearch,
    hasActiveFilters,
    handleClearFilters,
    goToPage,
    setCurrentPage,
    viewJobDetails,
    setViewType,
    dispatch,
    setApplicationsPerPage: (rows: number) => dispatch({ type: 'SET_APPLICATIONS_PER_PAGE', payload: rows }),
    isSearchApplied: state.searchApplied,
    setFilters: (filters: Partial<JobFilters>) => dispatch({ type: 'SET_FILTERS', payload: filters }),
    resetFilters: () => dispatch({ type: 'RESET_FILTERS' }),
    fetchJobs,
    setError: (error: string | null) => {
      dispatch({ type: 'SET_ERROR', payload: error });
      if (error) dispatch({ type: 'SET_TOAST', payload: { message: error, type: 'error' } });
    },
    setShowJobModal: (show: boolean) => dispatch({ type: 'SET_SHOW_JOB_MODAL', payload: show }),
    setSelectedJob: (job: Job | null) => dispatch({ type: 'SET_SELECTED_JOB', payload: job }),
    setToast: (t: { message: string; type: 'success' | 'error' } | null) => dispatch({ type: 'SET_TOAST', payload: t })
  };
};